/**
 * Media Queries Core - Shared Breakpoint Management
 *
 * This module implements a centralized breakpoint tracking system using the **Observer Pattern**.
 * It serves as a single source of truth for responsive breakpoints, shared across both modern
 * composables (useMediaQueries) and legacy vue-mq compatibility layer.
 *
 * ## Architecture Overview
 *
 * The core maintains reactive state using Vue refs and notifies all subscribers when the window
 * is resized. This allows multiple consumers to react to the same resize event without adding
 * duplicate listeners.
 *
 * ### Observer Pattern Components:
 * - **Subject**: The core module itself, which tracks window size and breakpoints
 * - **Observers**: Consumer hooks/adapters that subscribe to state changes
 * - **Notification**: All observers are notified when state changes (window resize)
 *
 * ### Why Vue Refs?
 * Using Vue's reactivity system provides:
 * - Type safety and reactivity guarantees from the Vue framework
 * - Visibility in Vue DevTools for debugging
 * - Consistency with the rest of the Vue ecosystem
 * - Prevents silent state mutations
 *
 * ### Lifecycle Management:
 * - Reference counting ensures the resize listener is added only when needed
 * - Multiple subscribers share a single resize listener for efficiency
 * - Cleanup happens automatically when all subscribers unsubscribe
 *
 * @module mediaQueriesCore
 */

import { ref } from 'vue';

/**
 * Available breakpoint identifiers.
 * Empty string ('') represents screens smaller than 'xs' (< 576px).
 */
export type Breakpoint = '' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '1xl' | '2xl' | '3xl';

/**
 * Ordered array of breakpoints from smallest to largest.
 * Used for comparison operations (isBigger, isUnder, etc.).
 */
export const breakpointOrder: Breakpoint[] = [
    '',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    '1xl',
    '2xl',
    '3xl',
];

/**
 * Breakpoint threshold widths in pixels.
 * A breakpoint is active when window width >= its threshold.
 *
 * @example
 * // Window width 768px → 'md' breakpoint
 * // Window width 1024px → 'lg' breakpoint
 */
export const breakpoints: Record<Breakpoint, number> = {
    '': 0,
    xs: 576,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '1xl': 1440,
    '2xl': 1512,
    '3xl': 1920,
};

/**
 * Legacy breakpoint type for vue-mq compatibility.
 * Excludes '' and '1xl' which were not part of the original vue-mq configuration.
 *
 * **Consumers:**
 * - **Modern breakpoints** (`breakpoints` above): used by the `useMediaQueries` composable
 *   for Composition API components.
 * - **Legacy breakpoints** (`legacyBreakpoints` below): used by `legacyBreakpointsAdapter`
 *   which powers the `$mq` global and layout flags (`mdLayout`, `lgLayout`, etc.) injected
 *   into Options API components via the legacy vue-mq plugin replacement.
 */
type LegacyBreakpoint = Exclude<Breakpoint, '' | '1xl'>;

/**
 * Legacy breakpoint thresholds using vue-mq's "max-width" approach.
 * Unlike modern breakpoints (which use min-width), legacy uses upper bounds.
 *
 * Used exclusively by {@link getLegacyBreakpointFromWidth} and the legacy adapter.
 * Does not include '1xl' to preserve original vue-mq behavior.
 *
 * @example
 * // Width 500px → 'xs' (500 < 576)
 * // Width 576px → 'sm' (576 < 576? No, 576 < 640? Yes)
 * // Width 700px → 'md' (700 < 576? No, < 640? No, < 768? Yes)
 */
const legacyBreakpoints: Record<LegacyBreakpoint, number> = {
    xs: 576,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1512,
    '3xl': Infinity,
};

/**
 * Immutable snapshot of the current window state.
 * This is what observers receive when notified of changes.
 */
type Snapshot = {
    width: number;
    height: number;
    currentBreakpoint: Breakpoint;
};

// ==========================================
// Observer Pattern - Internal State
// ==========================================

/**
 * Collection of observer callbacks (subscribers).
 * Each function receives a Snapshot when the window is resized.
 * @private
 */
const listeners = new Set<(snapshot: Snapshot) => void>();

/**
 * Number of active subscribers.
 * Tracks how many consumers are using the resize listener to ensure safe cleanup.
 * Plain number (not a ref) because it's an internal counter, not reactive UI state.
 * @private
 */
let refCount = 0;

/**
 * Whether the resize listener is currently attached to the window.
 * Plain boolean (not a ref) because it's an internal flag, not reactive UI state.
 * @private
 */
let isListening = false;

/**
 * Reactive state: Current window width in pixels.
 * Uses Vue ref for type safety and framework integration.
 * @private
 */
const width = ref(0);

/**
 * Reactive state: Current window height in pixels.
 * Uses Vue ref for type safety and framework integration.
 * @private
 */
const height = ref(0);

/**
 * Reactive state: Current breakpoint identifier.
 * Uses Vue ref for type safety and framework integration.
 * @private
 */
const currentBreakpoint = ref<Breakpoint>('');

// ==========================================
// Utility Functions
// ==========================================

/**
 * Gets current window dimensions.
 * Returns 0x0 in SSR environments.
 * @private
 */
const getWindowSize = () => {
    if (typeof window === 'undefined') {
        return { width: 0, height: 0 };
    }

    return { width: window.innerWidth, height: window.innerHeight };
};

/**
 * Determines the current breakpoint based on window width (modern min-width approach).
 *
 * Uses a "mobile-first" strategy: finds the largest breakpoint whose threshold
 * the width meets or exceeds.
 *
 * @param width - Window width in pixels
 * @returns The matching breakpoint identifier
 *
 * @example
 * getBreakpointFromWidth(500) // → ''
 * getBreakpointFromWidth(768) // → 'md'
 * getBreakpointFromWidth(1920) // → '3xl'
 */
export const getBreakpointFromWidth = (width: number): Breakpoint => {
    let current: Breakpoint = '';

    for (const breakpoint of breakpointOrder) {
        if (width >= breakpoints[breakpoint]) {
            current = breakpoint;
        }
    }

    return current;
};

/**
 * Determines the breakpoint using legacy vue-mq max-width logic.
 *
 * Uses a "desktop-first" strategy: finds the first breakpoint whose max threshold
 * the width fits within.
 *
 * @param width - Window width in pixels
 * @returns The matching legacy breakpoint (never returns '')
 *
 * @example
 * getLegacyBreakpointFromWidth(500) // → 'xs'
 * getLegacyBreakpointFromWidth(576) // → 'sm' (exclusive upper bound, matches vue-mq)
 * getLegacyBreakpointFromWidth(768) // → 'lg'
 * getLegacyBreakpointFromWidth(5000) // → '3xl'
 */
export const getLegacyBreakpointFromWidth = (width: number): Exclude<Breakpoint, ''> => {
    for (const [breakpoint, maxWidth] of Object.entries(legacyBreakpoints)) {
        if (width < maxWidth) {
            return breakpoint as Exclude<Breakpoint, ''>;
        }
    }

    return '3xl';
};

// ==========================================
// Observer Pattern - Core Implementation
// ==========================================

/**
 * Updates the internal reactive state based on current window dimensions.
 * Does not notify observers - call notify() separately if needed.
 * @private
 */
const refreshState = () => {
    const windowSize = getWindowSize();

    width.value = windowSize.width;
    height.value = windowSize.height;
    currentBreakpoint.value = getBreakpointFromWidth(windowSize.width);
};

/**
 * Notifies all registered observers of the current state.
 * Each observer receives a fresh snapshot copy.
 * @private
 */
const notify = () => {
    const snapshot = getSnapshot();

    for (const listener of listeners) {
        listener(snapshot);
    }
};

/**
 * Resize event handler.
 * Refreshes state and notifies all observers.
 * @private
 */
const handleResize = () => {
    refreshState();
    notify();
};

/**
 * Activates the resize listener (if not already active) and notifies observers.
 *
 * Uses reference counting: multiple calls to startResizeTracking() require matching
 * stopResizeTracking() calls before the listener is removed. This allows multiple
 * consumers to safely share the same resize listener.
 *
 * **refCount** tracks active consumers - it's a plain number (not a Vue ref) because
 * it's an internal lifecycle counter, not reactive UI state. No component needs to
 * watch or react to refCount changes.
 *
 * @example
 * // Component A mounts → startResizeTracking() (refCount: 0→1, listener added)
 * // Component B mounts → startResizeTracking() (refCount: 1→2, listener already active)
 * // Component A unmounts → stopResizeTracking() (refCount: 2→1, listener remains)
 * // Component B unmounts → stopResizeTracking() (refCount: 1→0, listener removed)
 */
export const startResizeTracking = () => {
    refCount += 1;

    if (!isListening && typeof window !== 'undefined') {
        window.addEventListener('resize', handleResize);
        isListening = true;
    }

    refreshState();
    notify();
};

/**
 * Decrements the reference count and removes the resize listener if no consumers remain.
 *
 * Safe to call multiple times - will not decrement below 0.
 */
export const stopResizeTracking = () => {
    if (refCount === 0) {
        return;
    }

    refCount -= 1;

    if (refCount === 0 && isListening && typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
        isListening = false;
    }
};

/**
 * Registers an observer to receive state change notifications.
 *
 * The observer will be called with a Snapshot whenever the window is resized.
 * Returns an unsubscribe function for cleanup.
 *
 * @param listener - Callback function that receives state snapshots
 * @returns Unsubscribe function to remove this observer
 *
 * @example
 * const unsubscribe = onBreakpointChange((snapshot) => {
 *   console.log('Current breakpoint:', snapshot.currentBreakpoint);
 * });
 *
 * // Later, cleanup:
 * unsubscribe();
 */
export const onBreakpointChange = (listener: (snapshot: Snapshot) => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

/**
 * Returns an immutable copy of the current state snapshot.
 *
 * Reads from Vue refs and creates a plain object snapshot for observers.
 *
 * @returns Current window dimensions and breakpoint
 */
export const getSnapshot = (): Snapshot => ({
    width: width.value,
    height: height.value,
    currentBreakpoint: currentBreakpoint.value,
});

// ==========================================
// Breakpoint Comparison Helpers
// ==========================================

/**
 * Gets the index of a breakpoint in the ordered list.
 * @private
 */
const getBreakpointIndex = (breakpoint: string) =>
    breakpointOrder.indexOf(breakpoint as Breakpoint);

/**
 * Checks if the current breakpoint exactly matches the target.
 *
 * @param current - Current breakpoint
 * @param target - Target breakpoint to compare against
 * @returns True if current === target
 *
 * @example
 * isBreakpoint('md', 'md') // → true
 * isBreakpoint('lg', 'md') // → false
 */
export const isBreakpoint = (current: Breakpoint, target: string): boolean => {
    return current === target;
};

/**
 * Checks if the current breakpoint is smaller than the target.
 *
 * @param current - Current breakpoint
 * @param target - Target breakpoint to compare against
 * @returns True if current < target in breakpoint order
 *
 * @example
 * isUnderBreakpoint('sm', 'lg') // → true
 * isUnderBreakpoint('lg', 'sm') // → false
 * isUnderBreakpoint('md', 'md') // → false
 */
export const isUnderBreakpoint = (current: Breakpoint, target: string): boolean => {
    const currentIndex = getBreakpointIndex(current);
    const targetIndex = getBreakpointIndex(target);

    return currentIndex < targetIndex || currentIndex === -1;
};

/**
 * Checks if the current breakpoint is larger than the target.
 *
 * @param current - Current breakpoint
 * @param target - Target breakpoint to compare against
 * @returns True if current > target in breakpoint order
 *
 * @example
 * isBiggerBreakpoint('xl', 'md') // → true
 * isBiggerBreakpoint('sm', 'lg') // → false
 * isBiggerBreakpoint('md', 'md') // → false
 */
export const isBiggerBreakpoint = (current: Breakpoint, target: string): boolean => {
    const currentIndex = getBreakpointIndex(current);
    const targetIndex = getBreakpointIndex(target);

    return currentIndex > targetIndex || currentIndex === -1;
};

/**
 * Checks if the current breakpoint is larger than or equal to the target.
 *
 * @param current - Current breakpoint
 * @param target - Target breakpoint to compare against
 * @returns True if current >= target in breakpoint order
 *
 * @example
 * isBiggerOrEqualBreakpoint('lg', 'md') // → true
 * isBiggerOrEqualBreakpoint('md', 'md') // → true
 * isBiggerOrEqualBreakpoint('sm', 'lg') // → false
 */
export const isBiggerOrEqualBreakpoint = (current: Breakpoint, target: string): boolean => {
    const currentIndex = getBreakpointIndex(current);
    const targetIndex = getBreakpointIndex(target);

    return currentIndex >= targetIndex || currentIndex === -1;
};
